/**
 * NASS Postpartum landing interactivity — works whether the landing is one section
 * or many separate nass-pp-* sections (each wrapped in .pp[data-nass-postpartum]).
 * Idempotent: queries the whole document, guards every binding per-element, and can be
 * re-run (Theme Editor section:load) safely. The IIFE itself runs once even if several
 * sections each include this script.
 */
(() => {
  if (window.__nassPpJs) return;
  window.__nassPpJs = true;

  const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let io = null;
  if (!reduce && 'IntersectionObserver' in window) {
    io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.setAttribute('data-rv', 'in');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
  }

  // Blocks that reveal on scroll (auto-tagged so section markup stays lean)
  const AUTO_RV = [
    '.pp-flp', '.pp-g-recover-imgs', '.pp-recover-card',
    '.pp-built-pic', '.pp-built-copy', '.pp-how-card', '.pp-stg', '.pp-g-inside',
    '.pp-g-price', '.pp-fineprint', '.pp-crsl-it', '.pp-app-ph', '.pp-app-video',
    '.pp-g-moms', '.pp-g-safe', '.pp-faq-list', '.pp-fin'
  ];

  const init = () => {
    // Auto-tag content blocks for reveal
    AUTO_RV.forEach((sel) => {
      document.querySelectorAll('.pp ' + sel).forEach((el) => {
        if (!el.hasAttribute('data-rv')) el.setAttribute('data-rv', '');
      });
    });

    // Reveal on scroll
    document.querySelectorAll('.pp [data-rv]').forEach((el) => {
      if (el.dataset.rvBound) return;
      el.dataset.rvBound = '1';
      if (io) io.observe(el);
      else el.setAttribute('data-rv', 'in');
    });

    // Flip cards + interactivity affordance
    document.querySelectorAll('.pp .pp-flp').forEach((card) => {
      if (card.dataset.flipBound) return;
      card.dataset.flipBound = '1';
      card.setAttribute('role', 'button');
      card.setAttribute('tabindex', '0');
      card.setAttribute('aria-pressed', 'false');
      const toggle = () => {
        const on = card.dataset.flip === '1';
        card.dataset.flip = on ? '' : '1';
        card.setAttribute('aria-pressed', on ? 'false' : 'true');
      };
      card.addEventListener('click', toggle);
      card.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') { e.preventDefault(); toggle(); }
      });
    });

    // One-time demo: the first card fully flips and returns, showing the cards turn
    if (!reduce) {
      const hintGrid = (grid) => {
        const card = grid.querySelector('.pp-flp');
        if (!card || card.dataset.hintDone || card.dataset.flip === '1') return;
        card.dataset.hintDone = '1';
        setTimeout(() => {
          if (card.dataset.flip === '1') return;
          const inner = card.querySelector('.pp-flp-in');
          card.classList.add('is-hint');
          const clear = () => { card.classList.remove('is-hint'); if (inner) inner.removeEventListener('animationend', clear); };
          if (inner) inner.addEventListener('animationend', clear);
          else setTimeout(clear, 2400);
        }, 480);
      };
      const hintIO = ('IntersectionObserver' in window)
        ? new IntersectionObserver((entries, obs) => entries.forEach((e) => {
            if (e.isIntersecting) { hintGrid(e.target); obs.unobserve(e.target); }
          }), { threshold: 0.35 })
        : null;
      document.querySelectorAll('.pp .pp-g-feel').forEach((grid) => {
        if (grid.dataset.hintBound) return;
        grid.dataset.hintBound = '1';
        if (hintIO) hintIO.observe(grid); else hintGrid(grid);
      });
    }

    // 4-stage tab switcher (scoped per stage group)
    document.querySelectorAll('.pp .pp-stg').forEach((stg) => {
      if (stg.dataset.stgBound) return;
      stg.dataset.stgBound = '1';
      const tabs = stg.querySelectorAll('.pp-stg-tab');
      const panels = stg.querySelectorAll('.pp-stg-panel');
      tabs.forEach((tab) => {
        tab.addEventListener('click', () => {
          const i = tab.dataset.i;
          tabs.forEach((t) => (t.dataset.on = t.dataset.i === i ? '1' : ''));
          panels.forEach((p) => (p.dataset.on = p.dataset.i === i ? '1' : ''));
        });
      });
    });

    // FAQ accordion
    document.querySelectorAll('.pp .pp-fq-q').forEach((btn) => {
      if (btn.dataset.faqBound) return;
      btn.dataset.faqBound = '1';
      btn.addEventListener('click', () => {
        const item = btn.closest('.pp-fq-i');
        if (item) item.dataset.open = item.dataset.open === '1' ? '' : '1';
      });
    });

    // Carousel dots — center-based, so the first/last dots work with scroll-snap + padding
    document.querySelectorAll('.pp .pp-crsl, .pp .pp-g-how').forEach((crsl) => {
      if (crsl.dataset.crslBound) return;
      crsl.dataset.crslBound = '1';
      const wrap = crsl.closest('.pp');
      const dots = wrap ? Array.from(wrap.querySelectorAll('.pp-dot')) : [];
      const items = Array.from(crsl.children);
      if (!dots.length || !items.length) return;
      // Dots map to scroll progress: first dot at the very start, last at the very end.
      const activeIndex = () => {
        const max = crsl.scrollWidth - crsl.clientWidth;
        if (max <= 0 || items.length < 2) return 0;
        return Math.round((crsl.scrollLeft / max) * (items.length - 1));
      };
      const setActive = (i) => dots.forEach((d) => (d.dataset.on = +d.dataset.i === i ? '1' : ''));
      const goTo = (i) => {
        const max = crsl.scrollWidth - crsl.clientWidth;
        if (max <= 0 || items.length < 2) return;
        crsl.scrollTo({ left: (max * i) / (items.length - 1), behavior: 'smooth' });
        setActive(i);
      };
      let holdUntil = 0;
      const hold = () => { holdUntil = Date.now() + 8000; };
      crsl.addEventListener('scroll', () => setActive(activeIndex()), { passive: true });
      dots.forEach((dot) => dot.addEventListener('click', () => { hold(); goTo(+dot.dataset.i); }));
      setActive(activeIndex());

      // Auto-advance (opt-in via data-auto="<ms>"): pauses on hover, touch,
      // dot clicks, hidden tab, and while any slide's sound is on.
      const autoMs = parseInt(crsl.dataset.auto, 10);
      if (autoMs && !reduce) {
        let hover = false;
        crsl.addEventListener('mouseenter', () => { hover = true; });
        crsl.addEventListener('mouseleave', () => { hover = false; });
        crsl.addEventListener('touchstart', hold, { passive: true });
        crsl.addEventListener('pointerdown', hold, { passive: true });
        setInterval(() => {
          if (hover || document.hidden || Date.now() < holdUntil) return;
          if (Array.from(crsl.querySelectorAll('video')).some((v) => !v.muted)) return;
          goTo((activeIndex() + 1) % items.length);
        }, autoMs);
      }
    });

    // Carousel videos: autoplay muted; the corner button toggles sound per slide
    document.querySelectorAll('.pp .pp-crsl-vid').forEach((vid) => {
      if (vid.dataset.vBound) return;
      vid.dataset.vBound = '1';
      const p = vid.play();
      if (p && p.catch) p.catch(() => {});
    });
    document.querySelectorAll('.pp .pp-crsl-snd').forEach((btn) => {
      if (btn.dataset.sndBound) return;
      btn.dataset.sndBound = '1';
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const vid = btn.closest('.pp-crsl-it')?.querySelector('.pp-crsl-vid');
        if (!vid) return;
        const willUnmute = vid.muted;
        document.querySelectorAll('.pp .pp-crsl-vid').forEach((v) => { v.muted = true; });
        document.querySelectorAll('.pp .pp-crsl-snd').forEach((b) => { b.dataset.muted = '1'; });
        if (willUnmute) {
          vid.muted = false;
          btn.dataset.muted = '';
          const p = vid.play();
          if (p && p.catch) p.catch(() => {});
        }
      });
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  if (window.Shopify && Shopify.designMode) document.addEventListener('shopify:section:load', init);
})();
